import { cn } from '@/lib/utils'
import { AlertTriangle, Info, XCircle, Clock } from 'lucide-react'

// Define types locally to avoid build dependency issues
type AgentNotificationSeverity = 'info' | 'warning' | 'error';

interface AgentNotificationContext {
  attempts?: number;
  issue?: string;
  triedApproaches?: string[];
}

interface AgentNotificationBannerProps {
  message: string
  severity: AgentNotificationSeverity
  context?: AgentNotificationContext
  className?: string
  onDismiss?: () => void
}

/**
 * AgentNotificationBanner - Displays agent progress/warning notifications
 *
 * Used when agent calls notify_user tool to inform user about:
 * - Progress updates (info)
 * - Difficulties encountered (warning)
 * - Blockers or errors (error)
 *
 * This is a NON-BLOCKING notification - agent continues working
 */
export function AgentNotificationBanner({
  message,
  severity,
  context,
  className,
  onDismiss
}: AgentNotificationBannerProps) {
  const severityConfig = {
    info: {
      icon: Info,
      bgClass: 'bg-primary/10 border-primary/30',
      textClass: 'text-primary dark:text-accent-foreground',
      iconClass: 'text-primary'
    },
    warning: {
      icon: AlertTriangle,
      bgClass: 'bg-amber-500/10 border-amber-500/30',
      textClass: 'text-amber-700 dark:text-amber-400',
      iconClass: 'text-amber-500'
    },
    error: {
      icon: XCircle,
      bgClass: 'bg-red-500/10 border-red-500/30',
      textClass: 'text-red-600 dark:text-red-400',
      iconClass: 'text-red-500'
    }
  }

  const config = severityConfig[severity]
  const Icon = config.icon

  return (
    <div className={cn(
      "rounded-lg border p-3 animate-fade-in transition-all duration-300",
      config.bgClass,
      className
    )}>
      <div className="flex items-start gap-3">
        {/* Icon */}
        <Icon className={cn("h-5 w-5 flex-shrink-0 mt-0.5", config.iconClass)} />

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Message */}
          <p className={cn("text-sm font-medium", config.textClass)}>
            {message}
          </p>

          {/* Context details */}
          {context && (
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              {context.attempts && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10">
                  <Clock className="h-3 w-3" />
                  Attempt {context.attempts}
                </span>
              )}
              {context.issue && (
                <span className="px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10">
                  {context.issue}
                </span>
              )}
            </div>
          )}

          {/* Tried approaches */}
          {context?.triedApproaches && context.triedApproaches.length > 0 && (
            <div className="mt-2 text-xs opacity-70">
              <span className="font-medium">Tried: </span>
              {context.triedApproaches.join(', ')}
            </div>
          )}
        </div>

        {/* Dismiss button */}
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="flex-shrink-0 p-1 rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
            aria-label="Dismiss notification"
          >
            <XCircle className="h-4 w-4 opacity-50 hover:opacity-100" />
          </button>
        )}
      </div>
    </div>
  )
}
